import { useState, useEffect } from 'react';
import { BaseDialog } from './BaseDialog';
import { useThemeStore, useTranslationStore, useTranslation, useUpdateStore, isPlaceholderVersion, selectUpdateAvailable } from '../../store';
import { CONSTANTS } from '../../constants/constants';
import Version from '../Version'
import toastService from '../../services/toastService';
import { clear as clearKeyvalStore } from 'idb-keyval/dist/compat.js';

// Default database name used by idb-keyval (see store/indexedDBStorage.js)
const KEYVAL_DB_NAME = 'keyval-store';

export const SettingsDialog = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { currentTheme, setTheme } = useThemeStore();
  const { language, setLanguage } = useTranslationStore();
  const [showToasts, setShowToasts] = useState(() => {
    const stored = localStorage.getItem('toastVisibility');
    return stored ? JSON.parse(stored) : false;
  });
  const [storageInfo, setStorageInfo] = useState({
    usage: 0,
    quota: 0,
    usagePercentage: 0,
    usedFormatted: '0 MB',
    availableFormatted: '0 MB',
    quotaFormatted: '0 MB',
    usageClass: 'text-success'
  });
  const [isClearing, setIsClearing] = useState(false);

  const localVersionInfo = useUpdateStore((state) => state.localVersionInfo);
  const remoteVersion = useUpdateStore((state) => state.remoteVersion);
  const isCheckingForUpdate = useUpdateStore((state) => state.isChecking);
  const isApplyingUpdate = useUpdateStore((state) => state.isApplying);
  const loadLocalVersion = useUpdateStore((state) => state.loadLocalVersion);
  const checkForUpdate = useUpdateStore((state) => state.checkForUpdate);
  const applyUpdate = useUpdateStore((state) => state.applyUpdate);

  const localVersion = localVersionInfo?.version;
  const hasLocalVersion = !isPlaceholderVersion(localVersion);
  const hasRemoteVersion = !isPlaceholderVersion(remoteVersion);
  const updateAvailable = useUpdateStore(selectUpdateAvailable);

  // Get storage information
  const getStorageInfo = async () => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 1; // Avoid division by zero
        const usagePercentage = (usage / quota) * 100;
        
        // Format bytes to human readable
        const formatBytes = (bytes) => {
          if (!bytes || bytes === 0) return '0 Bytes';
            
          const k = 1024;
          const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            
          if (bytes < k) {
            return bytes + ' Bytes';
          } else if (bytes < k * k) {
            return (bytes / k).toFixed(2) + ' KB';
          } else if (bytes < k * k * k) {
            return (bytes / (k * k)).toFixed(2) + ' MB';
          } else {
            return (bytes / (k * k * k)).toFixed(2) + ' GB';
          }
        };

        // Determine usage class based on percentage
        let usageClass = 'text-success';
        if (usagePercentage >= 85) {
          usageClass = 'text-danger';
        } else if (usagePercentage >= 70) {
          usageClass = 'text-warning';
        }

        return {
          usage,
          quota,
          usagePercentage,
          usedFormatted: formatBytes(usage),
          availableFormatted: formatBytes(quota - usage),
          quotaFormatted: formatBytes(quota),
          usageClass
        };
      } catch (error) {
        console.error('Error getting storage info:', error);
      }
    }
    return null;
  };

  // Update storage info
  const updateStorageInfo = async () => {
    const info = await getStorageInfo();
    if (info) {
      setStorageInfo(info);
    }
  };

  // The app's own idb-keyval connection stays open for the page's lifetime, so deleting
  // its database would be blocked: clear its store through that connection instead.
  // Any other blocked deletion is a failure, not a success.
  const deleteDatabase = (name) => {
    if (name === KEYVAL_DB_NAME) {
      return clearKeyvalStore();
    }
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error(`Deleting database "${name}" is blocked by an open connection`));
    });
  };

  // Clear app storage
  const clearStorage = async () => {
    if (!window.confirm(t('clearStorageConfirm') || 'Are you sure you want to clear all app data? This cannot be undone.')) {
      return;
    }

    setIsClearing(true);
    try {
      // Clear IndexedDB databases. One blocked database must not stop the rest of the
      // cleanup, so collect failures and report them once everything else is cleared.
      const failedDatabases = [];
      if (window.indexedDB) {
        const databases = await window.indexedDB.databases();
        for (const db of databases) {
          try {
            await deleteDatabase(db.name);
          } catch (error) {
            console.error(`Error clearing database "${db.name}":`, error);
            failedDatabases.push(db.name);
          }
        }
      }

      // Clear localStorage (except toast visibility preference)
      const toastVisibility = localStorage.getItem('toastVisibility');
      localStorage.clear();
      if (toastVisibility) {
        localStorage.setItem('toastVisibility', toastVisibility);
      }

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear caches if available
      if ('caches' in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(cacheNames.map(cacheName => window.caches.delete(cacheName)));
      }

      // Update storage info
      await updateStorageInfo();

      if (failedDatabases.length > 0) {
        throw new Error(`Could not clear databases: ${failedDatabases.join(', ')}`);
      }

      toastService.success(t('storageCleared') || 'Storage cleared successfully', { force: true });
      
      // Reload the page to ensure clean state
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Error clearing storage:', error);
      toastService.error(t('clearStorageError') || 'Error clearing storage', { force: true });
    } finally {
      setIsClearing(false);
    }
  };

  // Update storage info when dialog opens
  useEffect(() => {
    if (isOpen) {
      updateStorageInfo();
      loadLocalVersion();
    }
  }, [isOpen, loadLocalVersion]);

  const handleToastVisibilityChange = (e) => {
    const isVisible = e.target.checked;
    setShowToasts(isVisible);
    localStorage.setItem('toastVisibility', JSON.stringify(isVisible));
  };
  
  const copyLogs = async () => {
    try {
      const stored = localStorage.getItem('console_capture_history');
      if (stored) {
        await navigator.clipboard.writeText(stored);
        toastService.success(t('copied'), {force: true});
      }
    } catch (err) {
      console.error('Failed to copy logs: ' + err)
    }
  };

  return (
    <>
    <BaseDialog isOpen={isOpen} onClose={onClose} title={t('settings')} size="md" showClose="true" t={t}>
      <div className="p-3">

        {/* Theme Toggle Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3 border-bottom pb-2">{t('theme')}</h6>
          
          <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
            
            <div className="btn-group" role="group">
              <button type="button" className={`btn ${currentTheme === CONSTANTS.STANDARD_THEME ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setTheme(CONSTANTS.STANDARD_THEME)}>{t('light')}</button>
              <button type="button" className={`btn ${currentTheme === CONSTANTS.DARK_THEME ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setTheme(CONSTANTS.DARK_THEME)}>{t('dark')}</button>
            </div>
          </div>
        </div>
        <div className="spacer"></div>

        {/* Language Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3 border-bottom pb-2">{t('language')}</h6>
          
          <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
            
            <div className="btn-group" role="group">
              <button type="button" className={`btn ${language ===CONSTANTS.LOCALES.EN ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setLanguage(CONSTANTS.LOCALES.EN)}>{t('english')}</button>
              <button type="button" className={`btn ${language === CONSTANTS.LOCALES.FR ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setLanguage(CONSTANTS.LOCALES.FR)}>{t('french')}</button>
            </div>
          </div>
        </div>
        <div className="spacer"></div>
        
        {/* Memory Management Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3 border-bottom pb-2">{t('memoryManagement')}</h6>
          
          <div className="p-3 border rounded bg-light">
            {/* Storage Usage Bar */}
            <div className="storage-visualization mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">{t('storageUsage')}:</small>
                <small className={storageInfo.usageClass}> {storageInfo.usagePercentage.toFixed(1)}%</small>
              </div>
            </div>

            {/* Storage Details */}
            <div className="storage-details mb-3">
              <div className="row small text-muted">
                <div className="col-6">
                  <small className="text-muted">{t('totalSpace')}:</small>
                  <small className="fw-semibold"> {storageInfo.quotaFormatted}</small>
                </div>
                <div className="col-6">
                  <small className="text-muted">{t('spaceUsed')}:</small>
                  <small className="fw-semibold"> {storageInfo.usedFormatted}</small>
                </div>
                <div className="col-6 mt-2">
                  <small className="text-muted">{t('spaceAvailable')}:</small>
                  <small className={storageInfo.usageClass}> {storageInfo.availableFormatted}</small>
                </div>
              </div>
            </div>

            {/* Clear Storage Button */}
            <br/>
            <div className="d-grid">
              <button 
                type="button" 
                className={`btn ${
                  storageInfo.usagePercentage >= 70 ? 'btn-warning' : 'btn-outline-danger'
                }`}
                onClick={clearStorage}
                disabled={isClearing}
              >
                {isClearing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {t('clearing')}
                  </>
                ) : (
                  t('clearStorage')
                )}
              </button>
              <small className="text-muted mt-1 text-center" style={{ display: 'block' }}>
                {t('clearStorageWarning')}
              </small>
            </div>
          </div>
        </div>
        <br/>
        <div className="spacer"></div>
        
        {/* Toast Visibility Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3 border-bottom pb-2">{t('showToastMessages')}</h6>
          
          <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
            
            <div className="form-check form-switch">
              <input 
                className="form-check-input" 
                type="checkbox" 
                role="switch" 
                id="toastVisibility" 
                checked={showToasts}
                onChange={handleToastVisibilityChange}
              />
            </div>
          </div>
        </div>
        <div className="spacer"></div>
        
        {/* Debug Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3 border-bottom pb-2">{t('debug')}</h6>
          
          <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
            
            <div className="btn-group" role="group">
              <button type="button" className="btn btn-outline-secondary"
                onClick={copyLogs}>{t('copyLogs')}</button>
            </div>
          </div>
        </div>
        <div className="spacer"></div>
        <div className="spacer"></div>
        
        {/* Software Update Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3 border-bottom pb-2">{t('softwareUpdate')}</h6>

          <div className="p-3 border rounded bg-light">
            <div className="row small mb-3">
              <div className="col-6">
                <div className="text-muted">{t('currentVersion')}</div>
                <strong>{hasLocalVersion ? localVersion : t('na')}</strong>
              </div>
              <div className="col-6">
                <div className="text-muted">{t('latestVersion')}</div>
                <strong>{hasRemoteVersion ? remoteVersion : t('unknownVersion')}</strong>
              </div>
            </div>

            {hasRemoteVersion && (
              <div className={`alert ${updateAvailable ? 'alert-info' : 'alert-success'} py-2 px-3 small mb-3`}>
                {updateAvailable ? t('updateAvailableMessage') : t('upToDateMessage')}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={checkForUpdate}
                disabled={isCheckingForUpdate || isApplyingUpdate}
              >
                {isCheckingForUpdate ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {t('checking')}
                  </>
                ) : (
                  t('checkForUpdates')
                )}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyUpdate}
                disabled={!updateAvailable || isApplyingUpdate}
              >
                {isApplyingUpdate ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {t('updating')}
                  </>
                ) : (
                  t('updateNow')
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="spacer"></div>

        {/* Version Section */}
        <Version/>
        <div className="spacer"></div>
        <div className="spacer"></div>
      </div>
    </BaseDialog>
    </>
  );
};